/**
 * Contas puras do indicador de tempo real. Ficam fora do hook para serem
 * testáveis sem React nem socket.
 */

// Peso do jitter (RFC 3550, o mesmo do RTP): média móvel exponencial de 1/16.
// Absorve um pico isolado sem esconder degradação sustentada.
const JITTER_WEIGHT = 16;

// Faixas do RTT, em ms. Acima de RTT_BAD o tempo-real já é perceptível como
// travado por quem está atendendo.
export const RTT_OK = 300;
export const RTT_BAD = 1000;

/**
 * Jitter incremental: variação média do RTT entre pings consecutivos.
 *
 * jitter += (|rtt - rttAnterior| - jitter) / 16
 *
 * Guarda um número só — não precisa de histórico. Sem RTT anterior, o jitter
 * ainda não existe (0), porque variação exige dois pontos.
 */
export const nextJitter = (jitter, rtt, previousRtt) => {
  if (typeof previousRtt !== "number") return 0;
  const delta = Math.abs(rtt - previousRtt);
  return jitter + (delta - jitter) / JITTER_WEIGHT;
};

/**
 * Reparte o RTT entre o que foi gasto no servidor e o que foi rede.
 *
 * O servidor devolve quanto tempo levou entre receber e responder; o resto é
 * ida e volta na rede. Um pong sem essa informação (versão antiga do backend)
 * deixa `network` null em vez de fingir um número.
 */
export const splitRtt = (rtt, serverProcessMs) => {
  if (typeof serverProcessMs !== "number" || !Number.isFinite(serverProcessMs)) {
    return { server: null, network: null };
  }

  const server = Math.max(0, Math.round(serverProcessMs));
  return {
    server,
    // Piso em 0: o RTT nunca deveria ser menor que o processamento, mas
    // arredondamento nos dois lados pode produzir -1.
    network: Math.max(0, Math.round(rtt) - server)
  };
};

/**
 * Classifica a saúde do tempo-real. Uma perda de ping recente pesa mais que o
 * RTT: a resposta que não voltou é a evidência mais forte de que o tempo-real
 * está furado, mesmo com os pings anteriores rápidos.
 */
export const classifyQuality = ({ rtt, lostStreak = 0 } = {}) => {
  if (lostStreak > 0) return "bad";
  if (typeof rtt !== "number") return "unknown";
  if (rtt <= RTT_OK) return "good";
  if (rtt <= RTT_BAD) return "fair";
  return "bad";
};

/**
 * Aplica um pong à janela de métricas e devolve o estado novo.
 *
 * `sent` é o instante em que o ping saiu, medido pelo MESMO relógio que marca a
 * chegada — por isso o RTT não depende de o relógio do servidor estar certo, ao
 * contrário do atraso one-way que o indicador estimava antes.
 */
export const applyPong = (state, { sentAt, receivedAt, pong }) => {
  // Resposta de throttling do servidor: nao e uma medida de latencia, e um
  // aviso de que o ping foi recusado. Contabilizar viraria RTT falso.
  if (pong?.throttled) return { ...state, throttled: true };

  const rtt = Math.max(0, receivedAt - sentAt);
  const { server, network } = splitRtt(rtt, pong?.serverProcessMs);

  return {
    ...state,
    rtt: Math.round(rtt),
    serverMs: server,
    networkMs: network,
    loopLag: pong?.loopLag ?? null,
    jitter: Math.round(nextJitter(state.jitter ?? 0, rtt, state.rtt) * 10) / 10,
    lost: state.lost ?? 0,
    lostStreak: 0,
    throttled: false,
    samples: (state.samples ?? 0) + 1
  };
};

/**
 * Ping que estourou o prazo sem resposta. Mantém o último RTT conhecido — ele
 * ainda descreve a última vez que a coisa funcionou —, mas marca a falha.
 */
export const applyLostPing = state => ({
  ...state,
  lost: (state.lost ?? 0) + 1,
  lostStreak: (state.lostStreak ?? 0) + 1
});

export const initialMetrics = {
  rtt: null,
  serverMs: null,
  networkMs: null,
  jitter: 0,
  loopLag: null,
  lost: 0,
  lostStreak: 0,
  throttled: false,
  samples: 0,
  reconnects: 0
};
